import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Remembers the property the user is working in so sidebar links and
 * "Back" actions stay inside that property instead of jumping to org-wide lists.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationContextService {
  private readonly propertyIdSubject = new BehaviorSubject<string | null>(null);
  readonly propertyId$ = this.propertyIdSubject.asObservable();

  private readonly propertyScopedPaths = new Set([
    '/manage-leases',
    '/admin-invoices',
    '/property-expenses',
    '/property-statements',
    '/all-messages',
    '/external-payment',
    '/contact-info'
  ]);

  private readonly clearPropertyPaths = new Set([
    '/admin-landing',
    '/organization-settings',
    '/createUser',
    '/reports',
    '/organization-expenses',
    '/super-admin',
    '/lessee-landing'
  ]);

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.syncFromUrl());
    this.syncFromUrl();
  }

  get propertyId(): string | null {
    return this.propertyIdSubject.value;
  }

  get hasPropertyContext(): boolean {
    return !!this.propertyIdSubject.value;
  }

  setPropertyId(id: string | null | undefined): void {
    const next = id ? String(id) : null;
    if (this.propertyIdSubject.value !== next) {
      this.propertyIdSubject.next(next);
    }
  }

  clearProperty(): void {
    this.setPropertyId(null);
  }

  supportsPropertyScope(path: string): boolean {
    const normalized = this.normalizePath(path);
    return this.propertyScopedPaths.has(normalized);
  }

  /**
   * Navigate while preserving (or clearing) property context as appropriate.
   */
  navigate(path: string, options: { clearProperty?: boolean } = {}): void {
    const normalized = this.normalizePath(path);
    if (options.clearProperty || this.clearPropertyPaths.has(normalized)) {
      this.clearProperty();
      this.router.navigate([normalized]);
      return;
    }

    // Prefer property-scoped expenses when already inside a property.
    if (normalized === '/organization-expenses' && this.propertyId) {
      this.router.navigate(['/property-expenses'], {
        queryParams: { propertyId: this.propertyId }
      });
      return;
    }

    if (this.supportsPropertyScope(normalized) && this.propertyId) {
      this.router.navigate([normalized], {
        queryParams: { propertyId: this.propertyId }
      });
      return;
    }

    this.router.navigate([normalized]);
  }

  goToPropertyHub(propertyId?: string | null): void {
    const id = propertyId || this.propertyId;
    if (!id) {
      this.router.navigate(['/admin-landing']);
      return;
    }
    this.setPropertyId(id);
    this.router.navigate(['/view-property', id]);
  }

  goToDashboard(): void {
    this.clearProperty();
    this.router.navigate(['/admin-landing']);
  }

  goToManageLeases(propertyId?: string | null): void {
    const id = propertyId || this.propertyId;
    if (id) {
      this.setPropertyId(id);
      this.router.navigate(['/manage-leases'], { queryParams: { propertyId: id } });
      return;
    }
    this.router.navigate(['/manage-leases']);
  }

  goToMessages(propertyId?: string | null): void {
    const id = propertyId || this.propertyId;
    if (id) {
      this.setPropertyId(id);
      this.router.navigate(['/all-messages'], { queryParams: { propertyId: id } });
      return;
    }
    this.router.navigate(['/all-messages']);
  }

  private syncFromUrl(): void {
    const tree = this.router.parseUrl(this.router.url);
    const primary = tree.root.children['primary'];
    const segments = primary ? primary.segments.map(s => s.path) : [];
    const path = '/' + segments.join('/');
    const qPropertyId = tree.queryParams['propertyId'];

    if (qPropertyId) {
      this.setPropertyId(qPropertyId);
      return;
    }

    if (segments[0] === 'view-property' && segments[1]) {
      this.setPropertyId(segments[1]);
      return;
    }
    if (segments[0] === 'edit-property' && segments[1]) {
      this.setPropertyId(segments[1]);
      return;
    }
    if (segments[0] === 'start-lease' && segments[1]) {
      this.setPropertyId(segments[1]);
      return;
    }
    if (segments[0] === 'admin-landing' && segments[1] === 'view-property' && segments[2]) {
      this.setPropertyId(segments[2]);
      return;
    }

    if (this.clearPropertyPaths.has(path) || path === '/') {
      this.clearProperty();
    }
  }

  private normalizePath(path: string): string {
    if (!path) {
      return '/';
    }
    return path.startsWith('/') ? path : `/${path}`;
  }
}
